class _OperatorApplicationAPI {
  constructor() {
    this.versionPrefix = '/v2';
  }

  /**
   * Initialize.
   * TEMPORARY:
   *    The whole axios / auth should be replaced by proper install & usage.
   * @param {object} $axios - @nuxtjs/axios instance
   */
  init($axios) {
    this.API = $axios;
  }

  /**
   * Fetch Searches
   * @param {string} filters - The query filters
   * @return {array} - Array of collections data
   */
  async fetchOperatorApplication(id) {
    const applicationUrl = `${this.versionPrefix}/members/${id}/application`;
    try {
      const response = await this.API.get(applicationUrl, {});
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Create Member address
   * @param {object} payload - memberId: member id // profile: member profile w/ bio and portfolio links
   */
   async createMemberAddress(payload) {
    const requestURI = `${this.versionPrefix}/members/${payload.memberId}/address`;
    try {
      const response = await this.API.put(requestURI, {
        data: {
          type: 'address',
          attributes: payload.address,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update member phone
   * @param {object} state - the vuex state object
   * @param {object} payload - id: member id // phone: phone number
   */
  async patchPhone(payload) {
    const updatePhoneUrl = `${this.versionPrefix}/members/${payload.id}/phone`;
    try {
      const response = await this.API.patch(updatePhoneUrl, {
        data: {
          type: 'member',
          id: payload.id,
          attributes: {
            phone_1: payload.phone,
          },
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Confirm member phone
   * @param {object} state - the vuex state object
   * @param {object} payload - id: member id // code: verification code
   */
   async verifyPhone(payload) {
    const verifyPhoneUrl = `${this.versionPrefix}/members/${payload.id}/phone/verify`;
    try {
      const response = await this.API.post(verifyPhoneUrl, {
        data: {
          type: 'member',
          id: payload.id,
          attributes: {
            code: payload.code,
          },
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch Product Types
   * @param {string} filters - The query filters
   * @return {array} - Array of product types
   */
   async fetchProductTypes() {
    const typesUrl = `${this.versionPrefix}/products/types?per_page=all`;
    try {
      const response = await this.API.get(typesUrl);
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Fetch Product Subtypes
   * @param {string} filters - The query filters
   * @return {array} - Array of product subtypes
   */
   async fetchProductSubtypes() {
    const subtypesUrl = `${this.versionPrefix}/products/subtypes?per_page=all`;
    try {
      const response = await this.API.get(subtypesUrl);
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Upload an organization legal document
   * @param {object} state - the vuex state object
   * @param {object} payload - organizationId: organization id // documentId: document id // document: document file string
   */
   async uploadLegalDocument(payload) {
    const uploadDocUrl = `${this.versionPrefix}/organizations/${payload.organizationId}/documents/${payload.documentId}/upload`;
    try {
      const formData = new FormData();
      formData.append('document', payload.document);
      const response = await this.API.post(uploadDocUrl, formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch Organization documents
   * @param {string} filters - The query filters
   * @return {array} - Array of documents
   */
  async fetchOrganizationDocuments(organizationId) {
    const documentsUrl = `${this.versionPrefix}/organizations/${organizationId}/documents`;
    try {
      const response = await this.API.get(documentsUrl);
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Fetch Identification types
   * @param {string} filters - The query filters
   * @return {array} - Array of identification types
   */
   async fetchIdentificationTypes(organizationId) {
    const identificationUrl = `${this.versionPrefix}/organizations/${organizationId}/identifications/types`;
    try {
      const response = await this.API.get(identificationUrl);
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Update Member skills
   * @param {object} payload - memberId: member id // subtypes: list of subtype ids
   */
   async updateMemberSkills(payload) {
    const requestURI = `${this.versionPrefix}/members/${payload.memberId}/relationships/subtypes`;
    try {
      const response = await this.API.put(requestURI, {
        data: payload.subtypes.map((id) => ({
          type: 'product_subtype',
          id,
        })),
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch Equipments
   * @return {array} - Array of equipments
   */
   async fetchEquipments() {
    const equipmentsUrl = `${this.versionPrefix}/equipment?per_page=all`;
    try {
      const response = await this.API.get(equipmentsUrl);
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Update Member equipments
   * @param {object} payload - memberId: member id // equipments: list of equipments ids
   */
   async updateMemberEquipment(payload) {
    const requestURI = `${this.versionPrefix}/members/${payload.memberId}/relationships/equipment`;
    try {
      const response = await this.API.put(requestURI, {
        data: payload.equipment.map((id) => ({
          type: 'equipment',
          id,
        })),
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Fetch Languages
   * @return {array} - Array of languages
   */
   async fetchLanguages() {
    const languagesUrl = `${this.versionPrefix}/languages`;
    try {
      const response = await this.API.get(languagesUrl);
      return Promise.resolve(response.data);
    } catch (error) {
      return error.response.status === 403 ? Promise.reject() : Promise.reject(error);
    }
  }

  /**
   * Create Member profile
   * @param {object} payload - memberId: member id // profile: member profile w/ bio and portfolio links
   */
   async createMemberProfile(payload) {
    const requestURI = `${this.versionPrefix}/members/${payload.memberId}/profile`;
    try {
      const response = await this.API.post(requestURI, {
        data: payload.profile,
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update Member spoken languages
   * @param {object} payload - memberId: member id // languages: list of languages ids
   */
   async updateMemberLanguages(payload) {
    const requestURI = `${this.versionPrefix}/members/${payload.memberId}/relationships/spoken-languages`;
    try {
      const response = await this.API.put(requestURI, {
        data: payload.languages.map(({ id, code }) => ({
          type: 'language',
          id,
          code,
        })),
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Create Member applcation
   * @param {object} payload - memberId: member id // address: member address // utm: utm
   */
   async createMemberApplication(payload) {
    const requestURI = `${this.versionPrefix}/members/${payload.memberId}/application`;
    try {
      const response = await this.API.post(requestURI, {
        data: {
          type: 'operator_application',
          attributes: {
            utm: payload.utm,
          },
          relationships: {
            address: {
              data: {
                type: 'address',
                attributes: payload.address,
              },
            },
          },
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async finishIcOnboarding({
 bio, languages, utm, portfolios,
}) {
    const requestURI = `${this.versionPrefix}/image-creators/onboarding/finish`;
    try {
      const response = await this.API.post(requestURI, {
        data: {
          type: 'member',
          attributes: {
            bio,
            languages,
            utm,
            portfolios,
          },
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

const OperatorApplicationAPI = new _OperatorApplicationAPI();
export default OperatorApplicationAPI;
